import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon, AlertTriangleIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';
const CAPADashboard: React.FC = () => {
  const kpis = [{
    title: 'CAPA Closure %',
    value: '78%',
    change: '+12%',
    isPositive: true,
    subtitle: 'On-time closure rate'
  }, {
    title: 'Delayed CAPAs',
    value: '24',
    change: '-8',
    isPositive: true,
    subtitle: 'Past due date'
  }, {
    title: 'Repeat CAPAs',
    value: '6',
    change: '-3',
    isPositive: true,
    subtitle: 'Recurring issues'
  }];
  // CAPA Implementation Trend Data
  const capaImplementationTrendData = [{
    month: 'Jan',
    opened: 28,
    closed: 22,
    delayed: 12
  }, {
    month: 'Feb',
    opened: 25,
    closed: 24,
    delayed: 11
  }, {
    month: 'Mar',
    opened: 30,
    closed: 26,
    delayed: 13
  }, {
    month: 'Apr',
    opened: 27,
    closed: 28,
    delayed: 10
  }, {
    month: 'May',
    opened: 24,
    closed: 30,
    delayed: 8
  }, {
    month: 'Jun',
    opened: 22,
    closed: 26,
    delayed: 6
  }];
  // Root Cause Distribution Data
  const rootCauseDistributionData = [{
    name: 'Process Control',
    value: 32,
    percentage: 28.6
  }, {
    name: 'Training Gap',
    value: 24,
    percentage: 21.4
  }, {
    name: 'Equipment/Tooling',
    value: 18,
    percentage: 16.1
  }, {
    name: 'Material Quality',
    value: 15,
    percentage: 13.4
  }, {
    name: 'Documentation',
    value: 12,
    percentage: 10.7
  }, {
    name: 'Others',
    value: 11,
    percentage: 9.8
  }];
  const ROOT_CAUSE_COLORS = {
    'Process Control': '#ef4444',
    'Training Gap': '#f59e0b',
    'Equipment/Tooling': '#fbbf24',
    'Material Quality': '#3b82f6',
    Documentation: '#8b5cf6',
    Others: '#6b7280'
  };
  // Delays Heatmap Data
  const delaysHeatmapData = [{
    plant: 'Manesar',
    '0-7 days': 5,
    '8-15 days': 3,
    '16-30 days': 2,
    '>30 days': 1
  }, {
    plant: 'Chennai',
    '0-7 days': 4,
    '8-15 days': 2,
    '16-30 days': 1,
    '>30 days': 0
  }, {
    plant: 'Pune',
    '0-7 days': 6,
    '8-15 days': 4,
    '16-30 days': 3,
    '>30 days': 2
  }, {
    plant: 'Bangalore',
    '0-7 days': 4,
    '8-15 days': 3,
    '16-30 days': 2,
    '>30 days': 1
  }];
  // CAPA Listing Data
  const capaListingData = [{
    capaId: 'CAPA-2024-001',
    ncrId: 'NCR-2024-001',
    dateRaised: '2024-01-15',
    plant: 'Manesar',
    area: 'Production Floor',
    finding: 'Torque specification not followed on Line 3 assembly',
    rootCause: 'Training Gap',
    correctiveAction: 'Retraining completed for all operators on Line 3',
    preventiveAction: 'Updated work instruction with visual aids and error-proofing',
    responsiblePerson: 'Production Supervisor',
    targetDate: '2024-02-15',
    actualClosureDate: '2024-02-10',
    status: 'Closed',
    effectiveness: 'Verified',
    daysOpen: 26,
    delay: 0,
    verifiedBy: 'Quality Manager',
    remarks: 'No recurrence observed in 30-day monitoring period'
  }, {
    capaId: 'CAPA-2024-002',
    ncrId: 'NCR-2024-002',
    dateRaised: '2024-01-18',
    plant: 'Chennai',
    area: 'Warehouse',
    finding: 'Damaged components received from supplier',
    rootCause: 'Material Quality',
    correctiveAction: 'Supplier notified, replacement parts received',
    preventiveAction: 'Enhanced incoming inspection checklist implemented',
    responsiblePerson: 'Supply Chain Manager',
    targetDate: '2024-02-18',
    actualClosureDate: '2024-02-15',
    status: 'Closed',
    effectiveness: 'Verified',
    daysOpen: 28,
    delay: 0,
    verifiedBy: 'Quality Team A',
    remarks: 'Supplier packaging standards updated and verified'
  }, {
    capaId: 'CAPA-2024-003',
    ncrId: 'NCR-2024-003',
    dateRaised: '2024-01-20',
    plant: 'Pune',
    area: 'Quality Lab',
    finding: 'CMM calibration certificate expired',
    rootCause: 'Process Control',
    correctiveAction: 'External calibration completed, certificate received',
    preventiveAction: 'Automated calibration reminder system implemented',
    responsiblePerson: 'Lab Manager',
    targetDate: '2024-02-05',
    actualClosureDate: '',
    status: 'In Progress',
    effectiveness: 'Pending',
    daysOpen: 23,
    delay: 8,
    verifiedBy: '',
    remarks: 'Calibration complete, effectiveness verification pending'
  }, {
    capaId: 'CAPA-2024-004',
    ncrId: 'NCR-2024-004',
    dateRaised: '2024-01-22',
    plant: 'Bangalore',
    area: 'Line 2',
    finding: 'Parts found with dimensional deviation beyond tolerance',
    rootCause: 'Equipment/Tooling',
    correctiveAction: 'Tool replaced, affected parts reworked',
    preventiveAction: 'Tool life monitoring system enhanced with predictive alerts',
    responsiblePerson: 'Maintenance Head',
    targetDate: '2024-01-29',
    actualClosureDate: '',
    status: 'In Progress',
    effectiveness: 'Pending',
    daysOpen: 21,
    delay: 14,
    verifiedBy: '',
    remarks: 'Tool monitoring system installation in progress'
  }, {
    capaId: 'CAPA-2024-005',
    ncrId: 'NCR-2024-005',
    dateRaised: '2024-01-25',
    plant: 'Manesar',
    area: 'Chemical Storage',
    finding: 'Incompatible chemicals stored together',
    rootCause: 'Process Control',
    correctiveAction: 'Chemical segregation implemented immediately',
    preventiveAction: 'Storage area reorganized with clear labeling and barriers',
    responsiblePerson: 'Warehouse Supervisor',
    targetDate: '2024-02-01',
    actualClosureDate: '2024-01-30',
    status: 'Closed',
    effectiveness: 'Verified',
    daysOpen: 5,
    delay: 0,
    verifiedBy: 'Safety Officer',
    remarks: 'Critical safety issue resolved ahead of schedule'
  }, {
    capaId: 'CAPA-2024-006',
    ncrId: 'NCR-2024-006',
    dateRaised: '2024-01-28',
    plant: 'Chennai',
    area: 'Line 1',
    finding: 'First piece inspection not documented',
    rootCause: 'Documentation',
    correctiveAction: 'Inspection forms provided at all workstations',
    preventiveAction: 'Digital inspection system implementation planned',
    responsiblePerson: 'QC Inspector',
    targetDate: '2024-02-28',
    actualClosureDate: '',
    status: 'Open',
    effectiveness: 'Pending',
    daysOpen: 15,
    delay: 0,
    verifiedBy: '',
    remarks: 'Digital system under evaluation, temporary forms in use'
  }, {
    capaId: 'CAPA-2024-007',
    ncrId: 'NCR-2024-007',
    dateRaised: '2024-02-01',
    plant: 'Pune',
    area: 'Maintenance Shop',
    finding: 'Oil spills not cleaned immediately',
    rootCause: 'Process Control',
    correctiveAction: 'Daily cleaning checklist implemented',
    preventiveAction: 'Spill containment kits placed at strategic locations',
    responsiblePerson: 'Maintenance Head',
    targetDate: '2024-02-22',
    actualClosureDate: '',
    status: 'In Progress',
    effectiveness: 'Pending',
    daysOpen: 12,
    delay: 0,
    verifiedBy: '',
    remarks: 'Cleaning compliance being monitored daily'
  }, {
    capaId: 'CAPA-2024-008',
    ncrId: 'NCR-2024-008',
    dateRaised: '2024-02-03',
    plant: 'Bangalore',
    area: 'Production Floor',
    finding: 'Machine guard missing on CNC-5',
    rootCause: 'Process Control',
    correctiveAction: 'Temporary guard installed, permanent guard fabrication ongoing',
    preventiveAction: 'Maintenance checklist updated to include guard verification',
    responsiblePerson: 'Maintenance Head',
    targetDate: '2024-02-06',
    actualClosureDate: '',
    status: 'In Progress',
    effectiveness: 'Pending',
    daysOpen: 10,
    delay: 7,
    verifiedBy: '',
    remarks: 'Critical safety issue - permanent guard expected by 2024-02-15'
  }, {
    capaId: 'CAPA-2024-009',
    ncrId: 'NCR-2024-009',
    dateRaised: '2024-02-05',
    plant: 'Manesar',
    area: 'Warehouse',
    finding: 'Inventory labels faded and illegible',
    rootCause: 'Material Quality',
    correctiveAction: 'All affected labels replaced with durable material',
    preventiveAction: 'Durable label material specified for all future use',
    responsiblePerson: 'Warehouse Supervisor',
    targetDate: '2024-02-25',
    actualClosureDate: '',
    status: 'Open',
    effectiveness: 'Pending',
    daysOpen: 8,
    delay: 0,
    verifiedBy: '',
    remarks: 'New labeling system procurement in progress'
  }, {
    capaId: 'CAPA-2024-010',
    ncrId: 'NCR-2024-010',
    dateRaised: '2024-02-08',
    plant: 'Chennai',
    area: 'Loading Bay',
    finding: 'PPE not worn by forklift operators',
    rootCause: 'Training Gap',
    correctiveAction: 'Disciplinary action taken, retraining completed',
    preventiveAction: 'Daily PPE compliance checks implemented',
    responsiblePerson: 'Logistics Manager',
    targetDate: '2024-02-15',
    actualClosureDate: '',
    status: 'In Progress',
    effectiveness: 'Pending',
    daysOpen: 5,
    delay: 0,
    verifiedBy: '',
    remarks: 'Compliance monitoring showing 100% adherence'
  }, {
    capaId: 'CAPA-2024-011',
    ncrId: 'NCR-2024-011',
    dateRaised: '2024-02-10',
    plant: 'Pune',
    area: 'Line 3',
    finding: 'Material traceability not maintained',
    rootCause: 'Process Control',
    correctiveAction: 'Barcode scanning made mandatory for all material movements',
    preventiveAction: 'System audit scheduled weekly to ensure compliance',
    responsiblePerson: 'Production Supervisor',
    targetDate: '2024-02-28',
    actualClosureDate: '',
    status: 'Open',
    effectiveness: 'Pending',
    daysOpen: 3,
    delay: 0,
    verifiedBy: '',
    remarks: 'System training completed, monitoring compliance'
  }, {
    capaId: 'CAPA-2024-012',
    ncrId: 'NCR-2024-012',
    dateRaised: '2024-02-12',
    plant: 'Bangalore',
    area: 'Quality Lab',
    finding: 'Test results not recorded in system',
    rootCause: 'Documentation',
    correctiveAction: 'Backlog data entry completed',
    preventiveAction: 'Automated data capture system evaluation in progress',
    responsiblePerson: 'Lab Technician',
    targetDate: '2024-02-26',
    actualClosureDate: '',
    status: 'Open',
    effectiveness: 'Pending',
    daysOpen: 1,
    delay: 0,
    verifiedBy: '',
    remarks: 'Evaluating automated system to prevent future occurrences'
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Closed':
        return 'bg-green-100 text-green-700';
      case 'In Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Open':
        return 'bg-yellow-100 text-yellow-700';
      case 'Overdue':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getEffectivenessColor = (effectiveness: string) => {
    switch (effectiveness) {
      case 'Verified':
        return 'bg-green-100 text-green-700';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700';
      case 'Not Verified':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };
  const getDelayColor = (delay: number) => {
    if (delay === 0) return 'text-green-600';
    if (delay <= 7) return 'text-yellow-600';
    if (delay <= 14) return 'text-orange-600';
    return 'text-red-600';
  };
  return <div data-id="element-1298">
      <DescriptionPanel title="CAPA (Corrective & Preventive Actions)" description="Comprehensive CAPA management system tracking corrective and preventive actions from identification through verification. Monitor closure rates, analyze root causes, and identify systemic issues requiring attention to drive continuous improvement." insights={['CAPA closure rate improved to 78% - 12% improvement from last quarter', 'Delayed CAPAs reduced to 24 from 32 - focused effort on timely closures', 'Process Control identified as top root cause (28.6%) - systematic improvements needed', 'Repeat CAPAs down to 6 - effectiveness verification showing positive results']} data-id="element-1299" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-1300">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1301">
          CAPA Performance Metrics
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1302">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-1303">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-1304">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-1305">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-1306">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-1307">
                {kpi.isPositive ? <TrendingUpIcon size={16} className="mr-1" data-id="element-1308" /> : <TrendingDownIcon size={16} className="mr-1" data-id="element-1309" />}
                {kpi.change}
              </div>
            </Card>)}
        </div>
      </div>
      {/* CAPA Implementation Trend and Root Cause Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-1310">
        {/* CAPA Implementation Trend */}
        <Card className="p-6" data-id="element-1311">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1312">
            CAPA Implementation Trend
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1313">
            Monthly trend of CAPAs opened, closed, and delayed
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1314">
            <LineChart data={capaImplementationTrendData} data-id="element-1315">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1316" />
              <XAxis dataKey="month" tick={{
              fontSize: 12
            }} data-id="element-1317" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-1318" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1319" />
              <Legend data-id="element-1320" />
              <Line type="monotone" dataKey="opened" stroke="#ef4444" strokeWidth={3} name="Opened" dot={{
              r: 5
            }} data-id="element-1321" />
              <Line type="monotone" dataKey="closed" stroke="#10b981" strokeWidth={3} name="Closed" dot={{
              r: 5
            }} data-id="element-1322" />
              <Line type="monotone" dataKey="delayed" stroke="#f59e0b" strokeWidth={3} name="Delayed" dot={{
              r: 5
            }} data-id="element-1323" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1324">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-1325">
              <div className="text-center" data-id="element-1326">
                <div className="text-gray-600" data-id="element-1327">Current Month</div>
                <div className="text-xl font-bold text-gray-900" data-id="element-1328">Jun 2024</div>
              </div>
              <div className="text-center" data-id="element-1329">
                <div className="text-gray-600" data-id="element-1330">Opened</div>
                <div className="text-xl font-bold text-red-600" data-id="element-1331">22</div>
                <div className="text-xs text-gray-500" data-id="element-1332">this month</div>
              </div>
              <div className="text-center" data-id="element-1333">
                <div className="text-gray-600" data-id="element-1334">Closed</div>
                <div className="text-xl font-bold text-green-600" data-id="element-1335">26</div>
                <div className="text-xs text-gray-500" data-id="element-1336">this month</div>
              </div>
              <div className="text-center" data-id="element-1337">
                <div className="text-gray-600" data-id="element-1338">Delayed</div>
                <div className="text-xl font-bold text-orange-600" data-id="element-1339">6</div>
                <div className="text-xs text-gray-500" data-id="element-1340">past due</div>
              </div>
            </div>
          </div>
        </Card>
        {/* Root Cause Distribution */}
        <Card className="p-6" data-id="element-1341">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1342">
            Root Cause Distribution
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1343">
            Analysis of root causes across all CAPAs
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-1344">
            <PieChart data-id="element-1345">
              <Pie data={rootCauseDistributionData} cx="50%" cy="50%" labelLine={false} label={({
              name,
              percentage
            }) => `${name}: ${percentage}%`} outerRadius={120} fill="#8884d8" dataKey="value" data-id="element-1346">
                {rootCauseDistributionData.map((entry, index) => <Cell key={`cell-${index}`} fill={ROOT_CAUSE_COLORS[entry.name as keyof typeof ROOT_CAUSE_COLORS]} data-id="element-1347" />)}
              </Pie>
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} data-id="element-1348" />
              <Legend data-id="element-1349" />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1350">
            <div className="grid grid-cols-3 gap-4 text-xs" data-id="element-1351">
              <div className="text-center" data-id="element-1352">
                <div className="text-gray-600" data-id="element-1353">Process Control</div>
                <div className="text-sm font-bold text-red-600" data-id="element-1354">32</div>
                <div className="text-xs text-gray-500" data-id="element-1355">28.6%</div>
              </div>
              <div className="text-center" data-id="element-1356">
                <div className="text-gray-600" data-id="element-1357">Training Gap</div>
                <div className="text-sm font-bold text-orange-600" data-id="element-1358">24</div>
                <div className="text-xs text-gray-500" data-id="element-1359">21.4%</div>
              </div>
              <div className="text-center" data-id="element-1360">
                <div className="text-gray-600" data-id="element-1361">Equipment/Tooling</div>
                <div className="text-sm font-bold text-yellow-600" data-id="element-1362">18</div>
                <div className="text-xs text-gray-500" data-id="element-1363">16.1%</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Delays Heatmap */}
      <Card className="p-6 mb-8" data-id="element-1364">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1365">
          Delays Heatmap
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-1366">
          Plant-wise distribution of CAPA delays by duration
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-1367">
          <BarChart data={delaysHeatmapData} data-id="element-1368">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-1369" />
            <XAxis dataKey="plant" tick={{
            fontSize: 12
          }} data-id="element-1370" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-1371" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} data-id="element-1372" />
            <Legend data-id="element-1373" />
            <Bar dataKey="0-7 days" stackId="a" fill="#fbbf24" name="0-7 days" data-id="element-1374" />
            <Bar dataKey="8-15 days" stackId="a" fill="#f59e0b" name="8-15 days" data-id="element-1375" />
            <Bar dataKey="16-30 days" stackId="a" fill="#f97316" name="16-30 days" data-id="element-1376" />
            <Bar dataKey=">30 days" stackId="a" fill="#ef4444" name=">30 days" data-id="element-1377" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-1378">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-1379">
            <div className="text-center" data-id="element-1380">
              <div className="text-gray-600" data-id="element-1381">Total Delayed</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1382">24</div>
              <div className="text-xs text-gray-500" data-id="element-1383">CAPAs</div>
            </div>
            <div className="text-center" data-id="element-1384">
              <div className="text-gray-600" data-id="element-1385">0-7 days</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-1386">19</div>
              <div className="text-xs text-gray-500" data-id="element-1387">minor delay</div>
            </div>
            <div className="text-center" data-id="element-1388">
              <div className="text-gray-600" data-id="element-1389">8-15 days</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-1390">12</div>
              <div className="text-xs text-gray-500" data-id="element-1391">moderate</div>
            </div>
            <div className="text-center" data-id="element-1392">
              <div className="text-gray-600" data-id="element-1393">16-30 days</div>
              <div className="text-xl font-bold text-orange-700" data-id="element-1394">8</div>
              <div className="text-xs text-gray-500" data-id="element-1395">significant</div>
            </div>
            <div className="text-center" data-id="element-1396">
              <div className="text-gray-600" data-id="element-1397">&gt;30 days</div>
              <div className="text-xl font-bold text-red-600" data-id="element-1398">4</div>
              <div className="text-xs text-gray-500" data-id="element-1399">critical</div>
            </div>
          </div>
        </div>
      </Card>
      {/* CAPA Listing Table */}
      <Card className="p-6" data-id="element-1400">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-1401">
          CAPA Listing
        </h3>
        <div className="overflow-x-auto" data-id="element-1402">
          <table className="w-full" data-id="element-1403">
            <thead data-id="element-1404">
              <tr className="border-b-2 border-gray-300" data-id="element-1405">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1406">
                  CAPA ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1407">
                  NCR ID
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1408">
                  Date Raised
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1409">
                  Plant
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1410">
                  Area
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1411">
                  Finding
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1412">
                  Root Cause
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1413">
                  Corrective Action
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1414">
                  Preventive Action
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1415">
                  Responsible
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1416">
                  Target Date
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1417">
                  Closure Date
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1418">
                  Status
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1419">
                  Effectiveness
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1420">
                  Days Open
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1421">
                  Delay (days)
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1422">
                  Verified By
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-1423">
                  Remarks
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1424">
              {capaListingData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-1425">
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-1426">
                    {row.capaId}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-blue-600" data-id="element-1427">
                    {row.ncrId}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1428">
                    {row.dateRaised}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-1429">
                    {row.plant}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1430">
                    {row.area}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-1431">
                    {row.finding}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1432">
                    {row.rootCause}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-1433">
                    {row.correctiveAction}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-1434">
                    {row.preventiveAction}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1435">
                    {row.responsiblePerson}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1436">
                    {row.targetDate}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1437">
                    {row.actualClosureDate || '-'}
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1438">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getStatusColor(row.status)}`} data-id="element-1439">
                      {row.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1440">
                    <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-bold ${getEffectivenessColor(row.effectiveness)}`} data-id="element-1441">
                      {row.effectiveness}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1442">
                    <span className="text-sm font-bold text-gray-900" data-id="element-1443">
                      {row.daysOpen}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-1444">
                    <span className={`text-sm font-bold ${getDelayColor(row.delay)}`} data-id="element-1445">
                      {row.delay}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700" data-id="element-1446">
                    {row.verifiedBy || '-'}
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-700 max-w-xs" data-id="element-1447">
                    {row.remarks}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-1448">
          <div className="grid grid-cols-6 gap-4 text-sm" data-id="element-1449">
            <div className="text-center" data-id="element-1450">
              <div className="text-gray-600" data-id="element-1451">Total CAPAs</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-1452">12</div>
              <div className="text-xs text-gray-500" data-id="element-1453">tracked</div>
            </div>
            <div className="text-center" data-id="element-1454">
              <div className="text-gray-600" data-id="element-1455">Closed</div>
              <div className="text-xl font-bold text-green-600" data-id="element-1456">3</div>
              <div className="text-xs text-gray-500" data-id="element-1457">completed</div>
            </div>
            <div className="text-center" data-id="element-1458">
              <div className="text-gray-600" data-id="element-1459">In Progress</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-1460">5</div>
              <div className="text-xs text-gray-500" data-id="element-1461">ongoing</div>
            </div>
            <div className="text-center" data-id="element-1462">
              <div className="text-gray-600" data-id="element-1463">Open</div>
              <div className="text-xl font-bold text-yellow-600" data-id="element-1464">4</div>
              <div className="text-xs text-gray-500" data-id="element-1465">pending</div>
            </div>
            <div className="text-center" data-id="element-1466">
              <div className="text-gray-600" data-id="element-1467">Delayed</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-1468">3</div>
              <div className="text-xs text-gray-500" data-id="element-1469">past due</div>
            </div>
            <div className="text-center" data-id="element-1470">
              <div className="text-gray-600" data-id="element-1471">Avg Days Open</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-1472">13.1</div>
              <div className="text-xs text-gray-500" data-id="element-1473">days</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default CAPADashboard;